import getEnvVars from 'src/config/env';
import {
  getAccessToken,
  handleTokenRefresh,
  redirectToLoginOnSessionExpired,
  SessionExpiredError
} from 'src/utils/authService';

export { SessionExpiredError };

/** Relative API path (`/applications/...`) or absolute URL → full request URL. */
export const resolveApiUrl = (urlOrPath: string): string => {
  if (urlOrPath.startsWith('http://') || urlOrPath.startsWith('https://')) {
    return urlOrPath;
  }
  const { apiUrl } = getEnvVars();
  const path = urlOrPath.startsWith('/') ? urlOrPath : `/${urlOrPath}`;
  return `${apiUrl}${path}`;
};

/**
 * fetch() wrapper that mirrors axios httpRequest auth behavior:
 * attaches the access token, retries once after refresh on 401, and redirects to login when the session is dead.
 */
export const authenticatedFetch = async (
  url: string,
  options: RequestInit = {},
  isRetry = false
): Promise<Response> => {
  const requestUrl = resolveApiUrl(url);
  const token = getAccessToken();
  const headers = new Headers(options.headers);

  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  const response = await fetch(requestUrl, {
    ...options,
    headers,
    credentials: options.credentials ?? 'include'
  });

  if (response.status !== 401) {
    return response;
  }

  if (!isRetry) {
    try {
      await handleTokenRefresh();
      return await authenticatedFetch(url, options, true);
    } catch {
      redirectToLoginOnSessionExpired();
      throw new SessionExpiredError();
    }
  }

  redirectToLoginOnSessionExpired();
  throw new SessionExpiredError();
};

/** Download a file from an authenticated API endpoint (handles 401 → refresh → login redirect). */
export const downloadAuthenticatedFile = async (
  urlOrPath: string,
  filename: string,
  options: RequestInit = {}
): Promise<void> => {
  const response = await authenticatedFetch(urlOrPath, {
    method: 'GET',
    ...options,
    headers: {
      Accept: 'application/pdf, application/octet-stream, */*',
      ...(options.headers as Record<string, string> | undefined)
    }
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Download failed' }));
    throw new Error(errorData.message || `Download failed (${response.status})`);
  }

  const blob = await response.blob();
  const objectUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(objectUrl);
};
